'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAudioSegmentZip,
  readMediaFileMetadata,
  mergeMp3Files,
  splitMp3ByRanges,
  splitMp3IntoEqualParts,
  type AudioSegmentDefinition,
} from '@/lib/browser-audio-tools';
import { clamp, formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  acceptedVoiceNoteMimeTypes,
  formatBitrateKbps,
  formatDurationMs,
} from '@/components/tools/audio-tool-shared';
import { isMp3File, isVoiceNoteFile } from '@/lib/browser-audio-tools';

export type AudioSequenceToolVariant = 'mp3-merger' | 'mp3-splitter' | 'voice-memo-merger';

type LoadedMp3Item = {
  id: string;
  file: File;
  previewUrl: string;
  durationMs: number;
  approximateBitrateKbps: number | null;
};

const bitrateOptions = [128, 192, 256, 320];

export default function BrowserAudioSequenceTool({
  variant,
}: {
  variant: AudioSequenceToolVariant;
}) {
  const isMergeVariant = variant !== 'mp3-splitter';
  const isVoiceMerger = variant === 'voice-memo-merger';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [items, setItems] = useState<LoadedMp3Item[]>([]);
  const [selectedFile, setSelectedFile] = useState<LoadedMp3Item | null>(null);
  const [splitMode, setSplitMode] = useState<'equal' | 'custom'>('equal');
  const [partCount, setPartCount] = useState(3);
  const [bitrateKbps, setBitrateKbps] = useState(192);
  const [customStartMs, setCustomStartMs] = useState(0);
  const [customEndMs, setCustomEndMs] = useState(0);
  const [customRanges, setCustomRanges] = useState<AudioSegmentDefinition[]>([]);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }

      items.forEach((item) => URL.revokeObjectURL(item.previewUrl));

      if (selectedFile?.previewUrl) {
        URL.revokeObjectURL(selectedFile.previewUrl);
      }
    };
  }, [items, outputUrl, selectedFile]);

  useEffect(() => {
    if (variant !== 'mp3-splitter' || !selectedFile) {
      return;
    }

    setCustomStartMs(0);
    setCustomEndMs(Math.min(selectedFile.durationMs, 30_000));
    setCustomRanges([]);
  }, [selectedFile, variant]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function loadMp3Items(files: FileList | File[] | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!files?.length) {
      if (variant === 'mp3-splitter') {
        if (selectedFile?.previewUrl) {
          URL.revokeObjectURL(selectedFile.previewUrl);
        }
        setSelectedFile(null);
      } else {
        items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setItems([]);
      }
      return;
    }

    try {
      const loaded = await Promise.all(
        Array.from(files).map(async (file, index) => {
          const isValid = isVoiceMerger ? isVoiceNoteFile(file) : isMp3File(file);
          if (!isValid) {
            throw new Error(
              isVoiceMerger
                ? 'Choose voice notes or recordings only for this tool.'
                : 'Choose MP3 files only for this tool.',
            );
          }

          const metadata = await readMediaFileMetadata(file, 'audio');

          return {
            id: `${file.name}-${file.size}-${index}`,
            file,
            previewUrl: URL.createObjectURL(file),
            durationMs: metadata.durationMs,
            approximateBitrateKbps: metadata.approximateBitrateKbps,
          };
        }),
      );

      if (variant === 'mp3-splitter') {
        if (selectedFile?.previewUrl) {
          URL.revokeObjectURL(selectedFile.previewUrl);
        }
        setSelectedFile(loaded[0] ?? null);
      } else {
        items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setItems(loaded);
      }
    } catch (error) {
      if (variant === 'mp3-splitter') {
        if (selectedFile?.previewUrl) {
          URL.revokeObjectURL(selectedFile.previewUrl);
        }
        setSelectedFile(null);
      } else {
        items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setItems([]);
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : isVoiceMerger
            ? 'The voice notes could not be loaded.'
            : 'The MP3 files could not be loaded.',
      );
    }
  }

  function moveItem(itemId: string, direction: -1 | 1) {
    setItems((current) => {
      const index = current.findIndex((item) => item.id === itemId);

      if (index < 0) {
        return current;
      }

      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
    resetOutputState();
  }

  function addCustomRange() {
    if (!selectedFile) {
      return;
    }

    const startMs = clamp(customStartMs, 0, selectedFile.durationMs);
    const endMs = clamp(customEndMs, 0, selectedFile.durationMs);

    if (endMs <= startMs) {
      setErrorMessage('Each custom range needs an end time later than the start time.');
      return;
    }

    setErrorMessage(null);
    setCustomRanges((current) =>
      [...current, { startMs, endMs }].sort((left, right) => left.startMs - right.startMs),
    );
    resetOutputState();
  }

  function removeCustomRange(indexToRemove: number) {
    setCustomRanges((current) => current.filter((_, index) => index !== indexToRemove));
    resetOutputState();
  }

  async function runTool() {
    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      if (isMergeVariant) {
        if (items.length < 2) {
          throw new Error(
            isVoiceMerger
              ? 'Choose at least two voice notes to join.'
              : 'Choose at least two MP3 files to merge.',
          );
        }

        const credits = await consumeToolUsage(
          variant,
          isVoiceMerger
            ? 'Credits are not available for another voice-note join right now.'
            : 'Credits are not available for another MP3 merge right now.',
        );
        // mergeMp3Files decodes each input by its own real codec before concatenating, so it
        // merges mixed voice-note formats (an .opus note followed by a .m4a one) the same way it
        // merges plain MP3s. See its comment in browser-audio-tools.ts for why that matters.
        const blob = await mergeMp3Files({
          files: items.map((item) => item.file),
          bitrateKbps,
        });
        const nextUrl = URL.createObjectURL(blob);
        resetOutputState();
        setOutputUrl(nextUrl);
        setDownloadName(
          `${getBaseName(items[0]?.file.name || (isVoiceMerger ? 'voice-memo' : 'merged-audio'))}-${isVoiceMerger ? 'joined' : 'merged'}.mp3`,
        );
        setResultSize(blob.size);
        setCreditMessage(
          formatToolCreditSuccess(isVoiceMerger ? 'Voice-note join' : 'MP3 merge', credits),
        );
        return;
      }

      if (!selectedFile) {
        throw new Error('Choose one MP3 file to split first.');
      }

      const credits = await consumeToolUsage(
        'mp3-splitter',
        'Credits are not available for another MP3 split right now.',
      );
      const segments =
        splitMode === 'equal'
          ? await splitMp3IntoEqualParts({
              file: selectedFile.file,
              durationMs: selectedFile.durationMs,
              parts: partCount,
              bitrateKbps,
            })
          : await splitMp3ByRanges({
              file: selectedFile.file,
              ranges: customRanges,
              bitrateKbps,
            });

      if (!segments.length) {
        throw new Error('Add at least one valid split range before exporting.');
      }

      const zipBlob = await createAudioSegmentZip(segments);
      const nextUrl = URL.createObjectURL(zipBlob);
      resetOutputState();
      setOutputUrl(nextUrl);
      setDownloadName(`${getBaseName(selectedFile.file.name)}-split.zip`);
      setResultSize(zipBlob.size);
      setCreditMessage(formatToolCreditSuccess('MP3 split export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The audio export could not finish.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const totalDurationMs = useMemo(() => {
    if (isMergeVariant) {
      return items.reduce((sum, item) => sum + item.durationMs, 0);
    }

    return selectedFile?.durationMs ?? 0;
  }, [items, selectedFile, variant]);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>
            {isVoiceMerger
              ? 'Join multiple voice notes into one'
              : isMergeVariant
                ? 'Merge multiple MP3 files'
                : 'Split one MP3 into parts'}
          </h2>
          <p>
            {isVoiceMerger
              ? 'Arrange your voice notes in order and export one joined MP3 locally.'
              : isMergeVariant
                ? 'Arrange multiple MP3 files in order and export one merged MP3 locally.'
                : 'Split one MP3 into equal parts or custom ranges, then export all parts as a ZIP.'}
          </p>
        </div>
        <span className="tool-live-badge">{isVoiceMerger ? 'VOICE' : 'MP3'}</span>
      </div>

      {variant === 'mp3-splitter' ? (
        <div className="tool-live-routes" aria-label="Split mode">
          <button
            className={`tool-live-route ${splitMode === 'equal' ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setSplitMode('equal');
              resetOutputState();
            }}
            type="button"
          >
            Equal parts
          </button>
          <button
            className={`tool-live-route ${splitMode === 'custom' ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setSplitMode('custom');
              resetOutputState();
            }}
            type="button"
          >
            Custom ranges
          </button>
        </div>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void loadMp3Items(
                isMergeVariant
                  ? event.dataTransfer.files
                  : ([event.dataTransfer.files?.[0]].filter(Boolean) as File[]),
              );
            }}
            type="button"
          >
            <div>
              <strong>
                {isMergeVariant
                  ? items.length
                    ? `${items.length} ${isVoiceMerger ? 'voice notes' : 'MP3 files'} ready`
                    : isVoiceMerger
                      ? 'Choose voice notes or drop them here'
                      : 'Choose MP3 files or drop them here'
                  : selectedFile
                    ? selectedFile.file.name
                    : 'Choose one MP3 or drop it here'}
              </strong>
              <span>
                {isMergeVariant
                  ? items.length
                    ? `${formatDurationMs(totalDurationMs)} total`
                    : isVoiceMerger
                      ? 'Join WhatsApp, iPhone and Android voice notes into one MP3 locally'
                      : 'Merge podcast parts, lectures, and voice notes locally'
                  : selectedFile
                    ? `${formatFileSize(selectedFile.file.size)} • ${formatDurationMs(selectedFile.durationMs)}`
                    : 'Split long MP3 files into equal parts or custom clip ranges'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={isVoiceMerger ? acceptedVoiceNoteMimeTypes : 'audio/mpeg,.mp3'}
            className="tool-live-file-input"
            multiple={isMergeVariant}
            onChange={(event) => void loadMp3Items(event.target.files)}
            type="file"
          />

          {isMergeVariant ? (
            <div className="tool-audio-list">
              {items.map((item, index) => (
                <div className="tool-audio-list-item" key={item.id}>
                  <div>
                    <strong>{item.file.name}</strong>
                    <span>{`${formatDurationMs(item.durationMs)} • ${formatBitrateKbps(item.approximateBitrateKbps)}`}</span>
                  </div>
                  <div className="tool-audio-list-actions">
                    <button onClick={() => moveItem(item.id, -1)} type="button">
                      ↑
                    </button>
                    <button onClick={() => moveItem(item.id, 1)} type="button">
                      ↓
                    </button>
                    <span>{index + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {variant === 'mp3-splitter' && selectedFile && splitMode === 'equal' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Parts</span>
                <input
                  max="24"
                  min="2"
                  onChange={(event) => {
                    setPartCount(clamp(Number(event.target.value) || 2, 2, 24));
                    resetOutputState();
                  }}
                  type="number"
                  value={partCount}
                />
              </label>
            </div>
          ) : null}

          {variant === 'mp3-splitter' && selectedFile && splitMode === 'custom' ? (
            <>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Start</span>
                  <input
                    max={selectedFile.durationMs}
                    min="0"
                    onChange={(event) =>
                      setCustomStartMs(
                        clamp(Number(event.target.value) || 0, 0, selectedFile.durationMs),
                      )
                    }
                    step="100"
                    type="number"
                    value={customStartMs}
                  />
                </label>
                <label className="tool-live-field">
                  <span>End</span>
                  <input
                    max={selectedFile.durationMs}
                    min="0"
                    onChange={(event) =>
                      setCustomEndMs(
                        clamp(Number(event.target.value) || 0, 0, selectedFile.durationMs),
                      )
                    }
                    step="100"
                    type="number"
                    value={customEndMs}
                  />
                </label>
              </div>
              <div className="hero-actions tool-live-actions">
                <button
                  className="button button-ghost"
                  onClick={() => addCustomRange()}
                  type="button"
                >
                  Add range
                </button>
              </div>
              <div className="tool-audio-list">
                {customRanges.map((range, index) => (
                  <div
                    className="tool-audio-list-item"
                    key={`${range.startMs}-${range.endMs}-${index}`}
                  >
                    <div>
                      <strong>{`Part ${index + 1}`}</strong>
                      <span>{`${formatDurationMs(range.startMs)} → ${formatDurationMs(range.endMs)}`}</span>
                    </div>
                    <div className="tool-audio-list-actions">
                      <button onClick={() => removeCustomRange(index)} type="button">
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Bitrate</span>
              <select
                onChange={(event) => {
                  setBitrateKbps(Number(event.target.value));
                  resetOutputState();
                }}
                value={bitrateKbps}
              >
                {bitrateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} kbps
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={(isMergeVariant ? items.length < 2 : !selectedFile) || isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing
                ? isMergeVariant
                  ? isVoiceMerger
                    ? 'Joining…'
                    : 'Merging…'
                  : 'Splitting…'
                : isMergeVariant
                  ? isVoiceMerger
                    ? 'Join voice notes'
                    : 'Merge MP3 files'
                  : 'Export ZIP'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download {isMergeVariant ? 'MP3' : 'ZIP'}
              </a>
            ) : null}
          </div>

          {errorMessage ? <p className="tool-live-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="tool-live-success">{creditMessage}</p> : null}
        </div>

        <aside className="tool-live-preview">
          <div className="tool-live-preview-frame">
            {isMergeVariant ? (
              outputUrl ? (
                <audio controls src={outputUrl} />
              ) : (
                <div className="tool-live-panel-note">
                  <strong>Arrange your queue</strong>
                  <span>
                    {isVoiceMerger
                      ? 'Reorder the voice notes, then export one joined track.'
                      : 'Reorder the MP3 files, then export one merged track.'}
                  </span>
                </div>
              )
            ) : selectedFile ? (
              <audio controls src={selectedFile.previewUrl} />
            ) : (
              <div className="tool-live-panel-note">
                <strong>No source loaded</strong>
                <span>Add an MP3 to preview and split it.</span>
              </div>
            )}
          </div>

          <div className="tool-live-stats">
            <div>
              <strong>{isMergeVariant ? 'Files' : 'Source'}</strong>
              <span>
                {isMergeVariant
                  ? items.length || 'None yet'
                  : (selectedFile?.file.name ?? 'No file loaded')}
              </span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>{totalDurationMs ? formatDurationMs(totalDurationMs) : 'No file loaded'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Run the tool'}</span>
            </div>
            <div>
              <strong>Bitrate</strong>
              <span>{`${bitrateKbps} kbps target`}</span>
            </div>
            <div>
              <strong>{isMergeVariant ? 'Order' : 'Mode'}</strong>
              <span>
                {isMergeVariant
                  ? 'Top to bottom'
                  : splitMode === 'equal'
                    ? 'Equal parts'
                    : 'Custom ranges'}
              </span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
